import { yupResolver } from "@hookform/resolvers/yup"
import { Alert, Checkbox, FormControl, FormControlLabel, FormHelperText, Snackbar } from "@mui/material"
import { TFunction } from "i18next"
import { Controller, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import * as yup from "yup"
import { createRef, useEffect, useState } from "react"
// context
import useAuthenticationContext from "context/Authentication/useAuthenticationContext"
// hooks
import useToggleSnackbar from "hooks/useToggleSnackbar"
import useRegistration from "./hooks/useRegistration"
// components
import Button from "shared/components/Button"
import { TextField } from "shared/components/input"
import OnboardingLayout from "shared/components/OnboardingLayout"
import SimpleLink from "shared/components/SimpleLink"
// constants
import { ENTRY_POINT } from "constants/routes"
import { MODALS } from "constants/modals"
// assets
import Onboarding from "assets/images/backgrounds/Onboarding1.svg"
// utils
import { passwordValidator } from "utils/validators"
// style
import { SEfnOrDocumentText, SText, STitle } from "../styles"
import { validateEFN } from "utils/string"
import { TextMaskCustom } from "pages/Settings/Profile/Profile"
import { EFN_MUSK } from "constants/auth"

interface FormData {
  email: string
  password: string
  passwordConfirmation: string
  acceptedTermsAndConditions: boolean
  acceptedTrackingBehavior: boolean
  acceptedEmailNewsletter: boolean
  userEFN: string
  identityDocument?: File | undefined
}

interface RegistrationProps {
  isModalView?: boolean
}

const validator = (t: TFunction) =>
  yup
    .object()
    .shape({
      email: yup
        .string()
        .email(t("registration:inputFields:email:errors:format"))
        .required(t("registration:inputFields:email:errors:required")),
      passwordConfirmation: yup
        .string()
        .required(t("resetPassword:inputFields:password:errors:required"))
        .min(8, t("resetPassword:inputFields:password:errors:min"))
        .oneOf([yup.ref("password"), null], t("resetPassword:inputFields:passwordConfirmation:errors:match")),
      acceptedTermsAndConditions: yup
        .bool()
        .oneOf([true], t("registration:inputFields:acceptTermsAndConditions:errors:required")),
      acceptedTrackingBehavior: yup
        .bool()
        .oneOf([true], t("registration:inputFields:acceptedTrackingBehavior:errors:required")),
      // identityDocument and userEFN: at least one is required
      identityDocument: yup.mixed().notRequired(),
      userEFN: yup
        .string()
        .transform((value) => (value ? value.replace(/\s+/g, "") : ""))
        .test("is-valid-efn", t("settings:profile:inputFields:userEFN:errors:invalid"), function (value = "") {
          const { identityDocument } = this.parent

          // If document is provided, EFN is optional
          if (identityDocument) {
            return true
          }

          // If no document, EFN is required
          if (!value || value === "") {
            return this.createError({
              message: t("settings:profile:inputFields:userEFN:errors:required"),
              path: "userEFN",
            })
          }

          // Validate EFN format
          if (!/^\d+$/.test(value)) {
            return this.createError({
              message: t("settings:profile:inputFields:userEFN:errors:digits"),
              path: "userEFN",
            })
          }

          if (value.length !== 15) {
            return this.createError({
              message: t("settings:profile:inputFields:userEFN:errors:digitsAmount"),
              path: "userEFN",
            })
          }

          if (!validateEFN(value)) {
            return this.createError({
              message: t("settings:profile:inputFields:userEFN:errors:invalid"),
              path: "userEFN",
            })
          }

          return true
        }),
    })
    .concat(passwordValidator(t))
    .test("identity-check", t("registration:inputFields:identityDocument:errors:required"), function (value) {
      const { userEFN, identityDocument } = value
      const cleanedEFN = typeof userEFN === "string" ? userEFN.replace(/\s+/g, "") : ""

      // At least one must be provided
      if ((!cleanedEFN || cleanedEFN === "") && !identityDocument) {
        return this.createError({
          message: t("registration:inputFields:identityDocument:errors:required"),
          path: "identityDocument",
        })
      }

      return true
    })

const Registration = ({ isModalView }: RegistrationProps) => {
  const { t } = useTranslation()
  const { toRegister, isLoading, isError, error } = useRegistration({ isModalView })
  const { open, handleClose } = useToggleSnackbar(isError)
  const { user } = useAuthenticationContext()

  const identityFileRef = createRef<any>()

  const [identityButtonText, setIdentityButtonText] = useState<string>(
    t("registration:inputFields:identityFieldToggle:documentOption")
  )
  const [identity, setIdentity] = useState<"license" | "efn">("efn")

  const { handleSubmit, formState, control, watch, trigger, setValue } = useForm<FormData>({
    resolver: yupResolver(validator(t)),
    defaultValues: {
      email: user.email,
      password: "",
      passwordConfirmation: "",
      userEFN: "",
      identityDocument: undefined,
      acceptedTermsAndConditions: false,
      acceptedEmailNewsletter: false,
      acceptedTrackingBehavior: false,
    },
    reValidateMode: "onChange",
  })

  const toggleIdentityField = () => {
    // Clear both fields when toggling
    setValue("userEFN", "")
    setValue("identityDocument", undefined)

    // Clear the file input if it exists
    if (identityFileRef.current) {
      identityFileRef.current.value = null
    }

    // Toggle the identity mode
    if (identity === "efn") {
      setIdentity("license")
      setIdentityButtonText(t("registration:inputFields:identityFieldToggle:efnOption"))
    } else {
      setIdentity("efn")
      setIdentityButtonText(t("registration:inputFields:identityFieldToggle:documentOption"))
    }
  }

  const watchedEmail = watch("email")
  const watchedPass = watch("password")
  const watchedConfirmPass = watch("passwordConfirmation")
  const watchedAcceptedTerms = watch("acceptedTermsAndConditions")
  const watchedTrackingBehavior = watch("acceptedTrackingBehavior")
  const watchedUserEFN = watch("userEFN")
  const watchedIdentityDocument = watch("identityDocument")

  useEffect(() => {
    const passwordSchema = passwordValidator(t)

    if (!watchedPass || !passwordSchema.isValidSync({ password: watchedPass })) return

    trigger("passwordConfirmation")
  }, [watchedPass, watchedConfirmPass, trigger, t])

  const handleFormSubmit = ({
    email,
    password,
    acceptedTermsAndConditions,
    acceptedEmailNewsletter,
    acceptedTrackingBehavior,
    userEFN,
    identityDocument,
  }: FormData) => {
    const referral = sessionStorage.getItem("referral") || null

    toRegister({
      email,
      password,
      acceptedTermsAndConditions,
      acceptedEmailNewsletter,
      acceptedTrackingBehavior,
      userEFN,
      identityDocument,
      referral,
    })
  }

  const isFormValid =
    watchedEmail &&
    watchedPass &&
    watchedConfirmPass &&
    watchedAcceptedTerms &&
    watchedTrackingBehavior &&
    (watchedUserEFN || watchedIdentityDocument) &&
    !Object.keys(formState.errors).length

  return (
    <OnboardingLayout
      focused={true}
      showBackBtn
      rightBackgroundSrc={Onboarding}
      backBtnIconName="back"
      backPath={ENTRY_POINT}
      isModalView={isModalView}
      backModal={MODALS.AUTH.ENTRY_POINT}
    >
      <STitle>{t("registration:title")}</STitle>
      <SText variant="h5">{t("registration:text")}</SText>
      <form
        style={{ height: "100%", flexGrow: 1, display: "flex", flexDirection: "column" }}
        onSubmit={handleSubmit(handleFormSubmit)}
      >
        <Controller
          name="email"
          control={control}
          render={({
            field,
            formState: {
              errors: { email },
            },
          }) => (
            <TextField
              type="email"
              inputProps={field}
              error={!!email?.message}
              helperText={email?.message}
              sx={{ mb: 4 }}
              placeholder={t("registration:inputFields:email:placeholder")}
            />
          )}
        />
        <Controller
          name="password"
          control={control}
          render={({
            field,
            formState: {
              errors: { password },
            },
          }) => (
            <TextField
              onCopy={(e) => e.preventDefault()}
              type="password"
              inputProps={field}
              error={!!password?.message}
              helperText={password?.message}
              infoText={t("registration:inputFields:password:infoText")}
              sx={{ mb: 4 }}
              placeholder={t("registration:inputFields:password:placeholder")}
            />
          )}
        />
        <Controller
          name="passwordConfirmation"
          control={control}
          render={({
            field,
            formState: {
              errors: { passwordConfirmation },
            },
          }) => (
            <TextField
              onPaste={(e) => e.preventDefault()}
              type="password"
              inputProps={field}
              error={!!passwordConfirmation?.message}
              helperText={passwordConfirmation?.message}
              sx={{ mb: 4 }}
              placeholder={t("resetPassword:inputFields:passwordConfirmation:placeholder")}
              infoText={t("resetPassword:inputFields:passwordConfirmation:infoText")}
            />
          )}
        />
        {identity === "efn" ? (
          <Controller
            name="userEFN"
            control={control}
            render={({
              field,
              formState: {
                errors: { userEFN },
              },
            }) => (
              <TextField
                {...field}
                InputProps={{
                  inputComponent: TextMaskCustom as any,
                }}
                inputProps={{
                  mask: EFN_MUSK,
                  onBlur: field.onBlur,
                  onChange: field.onChange,
                }}
                error={!!userEFN?.message}
                helperText={userEFN?.message}
                sx={{ mb: 4 }}
                placeholder={t("settings:profile:inputFields:userEFN:placeholder")}
              />
            )}
          />
        ) : (
          <Controller
            name="identityDocument"
            control={control}
            render={({
              field: { onChange, value, ...field },
              formState: {
                errors: { identityDocument },
              },
            }) => (
              <FormControl fullWidth error={!!identityDocument?.message} sx={{ mb: 4 }}>
                <label htmlFor="identity-document-upload" style={{ cursor: "pointer" }}>
                  <Button
                    sx={{ mt: "auto", mb: 4 }}
                    disabled={isLoading}
                    color="secondary"
                    fullWidth
                    variant="contained"
                    onClick={() => identityFileRef.current?.click()}
                  >
                    {value ? (value as File).name : t("registration:inputFields:identityDocument:buttonText")}
                    <input
                      {...field}
                      type="file"
                      accept="application/pdf,image/*"
                      style={{ display: "none" }}
                      id="identity-document-upload"
                      ref={identityFileRef}
                      onChange={(e) => {
                        const file = e.target.files?.[0]
                        onChange(file)
                      }}
                    />
                  </Button>
                </label>
                {identityDocument?.message && <FormHelperText>{identityDocument.message}</FormHelperText>}
              </FormControl>
            )}
          />
        )}
        <SEfnOrDocumentText>{t("registration:inputFields:optionalOr:text")}</SEfnOrDocumentText>
        <Button
          sx={{ mt: "auto", mb: 4 }}
          // type="submit"
          disabled={isLoading}
          color="secondary"
          fullWidth
          variant="contained"
          onClick={toggleIdentityField}
        >
          {identityButtonText}
        </Button>
        <FormControl sx={{ mb: 5 }} error={!!formState.errors.acceptedTermsAndConditions?.message}>
          <FormControlLabel
            control={
              <Controller
                name="acceptedTermsAndConditions"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    color="secondary"
                    {...field}
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                    sx={{ alignSelf: "flex-start", paddingY: 0 }}
                  />
                )}
              />
            }
            label={
              <div>
                <SimpleLink
                  href="https://medudy.com/allgemeine-geschaeftsbedingungen"
                  target="_blank"
                  sx={[
                    ({ palette: { green } }) => ({
                      color: green.hyperpop[400],
                      "&:hover": {
                        color: green.hyperpop[600],
                      },
                    }),
                  ]}
                >
                  {t("registration:inputFields:acceptTermsAndConditions:placeholder:button1")}
                </SimpleLink>
                {t("registration:inputFields:acceptTermsAndConditions:placeholder:text1")}
                <SimpleLink
                  href="https://www.medudy.com/datenschutz"
                  target="_blank"
                  sx={[
                    ({ palette: { green } }) => ({
                      color: green.hyperpop[400],
                      "&:hover": {
                        color: green.hyperpop[600],
                      },
                    }),
                  ]}
                >
                  {t("registration:inputFields:acceptTermsAndConditions:placeholder:button2")}
                </SimpleLink>
                {t("registration:inputFields:acceptTermsAndConditions:placeholder:text2")}
              </div>
            }
          />
          <FormHelperText sx={{ mt: "-8px", mb: 0 }}>
            {formState.errors.acceptedTermsAndConditions?.message}
          </FormHelperText>
        </FormControl>
        <FormControl sx={{ mb: 5 }} error={!!formState.errors.acceptedTrackingBehavior?.message}>
          <FormControlLabel
            control={
              <Controller
                name="acceptedTrackingBehavior"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    color="secondary"
                    {...field}
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                    sx={{ alignSelf: "flex-start", paddingY: 0 }}
                  />
                )}
              />
            }
            label={
              <div>
                {t("registration:inputFields:acceptedTrackingBehavior:placeholder:text1")}
                <SimpleLink
                  href="https://www.medudy.com/datenschutz"
                  target="_blank"
                  sx={[
                    ({ palette: { green } }) => ({
                      color: green.hyperpop[400],
                      "&:hover": {
                        color: green.hyperpop[600],
                      },
                    }),
                  ]}
                >
                  {t("registration:inputFields:acceptedTrackingBehavior:placeholder:button1")}
                </SimpleLink>
              </div>
            }
          />
          <FormHelperText sx={{ mt: "-8px", mb: 0 }}>
            {formState.errors.acceptedTrackingBehavior?.message}
          </FormHelperText>
        </FormControl>
        <FormControl sx={{ mb: 6 }} error={!!formState.errors.acceptedEmailNewsletter?.message}>
          <FormControlLabel
            control={
              <Controller
                name="acceptedEmailNewsletter"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    color="secondary"
                    {...field}
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                    sx={{ alignSelf: "flex-start", paddingY: 0 }}
                  />
                )}
              />
            }
            label={t("registration:inputFields:acceptedEmailNewsletter:placeholder")}
          />
        </FormControl>
        <Button
          sx={{ mt: "auto" }}
          type="submit"
          disabled={!isFormValid || isLoading}
          color="secondary"
          fullWidth
          variant="contained"
        >
          {t("registration:submitButtonText")}
        </Button>
      </form>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        message={error}
        onClose={handleClose}
      >
        <Alert severity="error" sx={{ width: "100%" }} onClose={handleClose} closeText="Close">
          {error}
        </Alert>
      </Snackbar>
    </OnboardingLayout>
  )
}

export default Registration
